import React, { useState, useRef } from 'react';

// ★ 需求5：批次匯入代付紀錄
// 欄位格式：日期、代付人姓名、被代付人姓名、備註（備註可省略）
// 金額由系統從被代付人的 baseRate 自動帶入，並驗證必須一致（選項A）
export default function ImportProxyModal({ students, onImport, getBalance, onClose }) {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState('input'); // input | preview | done
  const [importCount, setImportCount] = useState(0);
  const fileRef = useRef();

  // 只有設定了 baseRate 且未畢業的學員才能參與代付
  const activeStudents = students.filter(s => !s.graduated);

  function parseCSV(text) {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;

    const sep = lines[0].includes('\t') ? '\t' : ',';
    const firstLine = lines[0];
    const hasHeader = /日期|代付|date/i.test(firstLine);
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const results = [];
    const errs = [];

    dataLines.forEach((line, idx) => {
      const cols = line.split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
      // 欄位：日期(0)、代付人(1)、被代付人(2)、備註(3)
      const [date, payerName, beneficiaryName, notes] = cols;
      const rowNum = idx + (hasHeader ? 2 : 1);

      if (!date || !payerName || !beneficiaryName) {
        errs.push(`第 ${rowNum} 行：缺少日期、代付人或被代付人`);
        return;
      }

      if (payerName === beneficiaryName) {
        errs.push(`第 ${rowNum} 行：代付人與被代付人不能是同一人（${payerName}）`);
        return;
      }

      const payer = activeStudents.find(s => s.name === payerName);
      const beneficiary = activeStudents.find(s => s.name === beneficiaryName);

      if (!payer) {
        errs.push(`第 ${rowNum} 行：找不到代付人「${payerName}」`);
        return;
      }
      if (!beneficiary) {
        errs.push(`第 ${rowNum} 行：找不到被代付人「${beneficiaryName}」`);
        return;
      }

      const beneficiaryBaseRate = Number(beneficiary.baseRate) || 0;
      if (beneficiaryBaseRate === 0) {
        errs.push(`第 ${rowNum} 行：「${beneficiaryName}」尚未設定單堂定價，無法計算代付金額`);
        return;
      }

      // 計算代付人目前餘額（無行事曆資料，usedLessons 用 0 估算）
      const payerBalance = getBalance(payer, 0) ?? 0;
      const balanceOk = payerBalance >= beneficiaryBaseRate;

      // 日期格式統一為 YYYY-MM-DD（補零）
      const rawDate = date.replace(/\//g, '-').trim();
      const dateParts = rawDate.split('-');
      const normalizedDate = dateParts.length === 3
        ? `${dateParts[0]}-${String(dateParts[1]).padStart(2,'0')}-${String(dateParts[2]).padStart(2,'0')}`
        : rawDate;

      // ★ 重複偵測：同代付人、同被代付人、同日期
      const isDuplicate = (payer.payments || []).some(p =>
        p.isProxy &&
        p.proxyType === 'payer' &&
        p.date === normalizedDate &&
        p.linkedId && (() => {
          // 找到這個 linkedId 對應的被代付人
          const linked = students.find(s =>
            (s.payments || []).some(bp =>
              bp.linkedId === p.linkedId && bp.proxyType === 'beneficiary' && s.id === beneficiary.id
            )
          );
          return !!linked;
        })()
      );

      results.push({
        date: normalizedDate,
        payerName,
        beneficiaryName,
        payerId: payer.id,
        beneficiaryId: beneficiary.id,
        beneficiaryBaseRate,
        payerBalance,
        balanceOk,
        notes: notes || '',
        matched: true,
        isDuplicate,
      });
    });

    setParsed(results);
    setErrors(errs);
    setStep('preview');
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setCsvText(ev.target.result);
      parseCSV(ev.target.result);
    };
    reader.readAsText(file, 'UTF-8');
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text');
    setCsvText(text);
    parseCSV(text);
  }

  // 跳過重複的筆數
  const importableRows = parsed.filter(r => r.matched && !r.isDuplicate);
  const insufficientRows = parsed.filter(r => r.matched && !r.isDuplicate && !r.balanceOk);
  const duplicateCount = parsed.filter(r => r.isDuplicate).length;

  function doImport() {
    onImport(importableRows.map(r => ({
      payerId: r.payerId,
      beneficiaryId: r.beneficiaryId,
      beneficiaryBaseRate: r.beneficiaryBaseRate,
      date: r.date,
      notes: r.notes,
    })));
    setImportCount(importableRows.length);
    setStep('done');
  }

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>批次匯入代付紀錄</span>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '18px 20px', maxHeight: 520, overflowY: 'auto' }}>

          {step === 'input' && (
            <>
              <p style={{ fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.8 }}>
                欄位順序：<strong>日期、代付人姓名、被代付人姓名、備註</strong>（備註可省略）<br/>
                金額由系統從被代付人的「單堂定價」自動帶入，無需填寫。<br/>
                <span style={{ color: '#7b1fa2' }}>代付人和被代付人都必須已設定「單堂定價」。</span>
              </p>

              <div style={{ background: '#f8f0ff', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#7b1fa2' }}>
                <strong>範例格式：</strong><br/>
                <code style={{ fontFamily: 'monospace', display: 'block', marginTop: 4, color: '#555' }}>
                  日期　　代付人　被代付人　備註<br/>
                  2025-06-10　林群欽　大康　家人代付<br/>
                  2025-06-17　林群欽　大康
                </code>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <button style={S.uploadBtn} onClick={() => fileRef.current.click()}>
                  上傳 CSV 檔案
                </button>
                <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFile} />
                <span style={{ fontSize: 12, color: '#aaa', alignSelf: 'center' }}>或直接在下方貼上資料</span>
              </div>

              <textarea
                style={S.textarea}
                placeholder={'貼上 Excel 複製的資料，例如：\n2025-06-10\t林群欽\t大康\t家人代付'}
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                onPaste={handlePaste}
              />

              <button
                style={{ ...S.btnP, marginTop: 10, opacity: csvText.trim() ? 1 : 0.5 }}
                onClick={() => parseCSV(csvText)}
                disabled={!csvText.trim()}
              >
                解析資料
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              {/* 統計摘要 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
                <div style={S.statCard}>
                  <div style={S.statLabel}>共解析</div>
                  <div style={S.statValue}>{parsed.length} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: importableRows.length > 0 ? '#f3e8fd' : '#f5f5f5' }}>
                  <div style={S.statLabel}>可匯入</div>
                  <div style={{ ...S.statValue, color: '#7b1fa2' }}>{importableRows.length} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: duplicateCount > 0 ? '#fef9e7' : '#f5f5f5' }}>
                  <div style={S.statLabel}>重複跳過</div>
                  <div style={{ ...S.statValue, color: duplicateCount > 0 ? '#b46309' : '#aaa' }}>{duplicateCount} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: insufficientRows.length > 0 ? '#fef9e7' : '#f5f5f5' }}>
                  <div style={S.statLabel}>餘額不足</div>
                  <div style={{ ...S.statValue, color: insufficientRows.length > 0 ? '#b46309' : '#aaa' }}>{insufficientRows.length} 筆</div>
                </div>
              </div>

              {errors.length > 0 && (
                <div style={{ background: '#fce8e6', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#c5221f' }}>
                  {errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              {/* 預覽表格 */}
              <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8f8f8' }}>
                      <th style={S.th}>狀態</th>
                      <th style={S.th}>日期</th>
                      <th style={S.th}>代付人</th>
                      <th style={S.th}>被代付人</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>金額</th>
                      <th style={S.th}>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r, i) => (
                      <tr key={i} style={{
                        background: r.isDuplicate ? '#fffbf0' : r.balanceOk ? '#fff' : '#fff8f0'
                      }}>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          {r.isDuplicate
                            ? <span style={{ color: '#b46309', fontWeight: 500 }} title="重複資料，將跳過">⚠</span>
                            : <span style={{ color: '#7b1fa2', fontWeight: 500 }}>✓</span>
                          }
                        </td>
                        <td style={{ ...S.td, color: r.isDuplicate ? '#b46309' : 'inherit' }}>{r.date}</td>
                        <td style={{ ...S.td, fontWeight: 500, color: r.isDuplicate ? '#b46309' : '#222' }}>{r.payerName}</td>
                        <td style={S.td}>{r.beneficiaryName}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: r.isDuplicate ? '#b46309' : '#7b1fa2', fontWeight: 500 }}>
                          ${r.beneficiaryBaseRate.toLocaleString()}
                        </td>
                        <td style={{ ...S.td, color: '#888' }}>{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {duplicateCount > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#b46309', background: '#fef9e7', borderRadius: 8, padding: '8px 12px' }}>
                  ⚠ 偵測到 {duplicateCount} 筆重複資料（同代付人、同被代付人、同日期），匯入時將自動跳過。若資料有誤請按「← 重新貼上」修改。
                </div>
              )}

              {insufficientRows.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#b46309', background: '#fef9e7', borderRadius: 8, padding: '8px 12px' }}>
                  ⚠ {insufficientRows.length} 筆代付人餘額不足，匯入後將顯示負數並標記催費。
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <button style={S.btnS} onClick={() => setStep('input')}>← 重新貼上</button>
              </div>
            </>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 500, color: '#7b1fa2', marginBottom: 8 }}>
                成功匯入 {importCount} 筆代付紀錄
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                代付人與被代付人的餘額均已自動更新
              </div>
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnS} onClick={onClose}>
            {step === 'done' ? '關閉' : '取消'}
          </button>
          {step === 'preview' && importableRows.length > 0 && (
            <button style={S.btnP} onClick={doImport}>
              匯入 {importableRows.length} 筆代付紀錄
            </button>
          )}        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 620, maxWidth: '97vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '0.5px solid #eee', flexShrink: 0 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid #eee', flexShrink: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer', lineHeight: 1 },
  textarea: { width: '100%', height: 140, border: '0.5px solid #ccc', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' },
  uploadBtn: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#444', cursor: 'pointer' },
  statCard: { background: '#f5f5f5', borderRadius: 8, padding: '10px 12px' },
  statLabel: { fontSize: 11, color: '#888', marginBottom: 3 },
  statValue: { fontSize: 20, fontWeight: 500, color: '#222' },
  th: { padding: '8px 10px', textAlign: 'left', fontWeight: 500, color: '#666', borderBottom: '0.5px solid #e0e0e0' },
  td: { padding: '7px 10px', borderBottom: '0.5px solid #f0f0f0', fontSize: 12 },
  btnS: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' },
  btnP: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#7b1fa2', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' },
};
